import React, { useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import LoginBackground from '../components/LoginBackground';
import { motion, useAnimation } from 'framer-motion';
import { 
  LogIn, 
  Shield, 
  Lock, 
  User, 
  Sparkles,
  Zap,
  Star,
  CheckCircle
} from 'lucide-react';

const Login = () => {
  const { loginWithGoogle } = useAuth();
  const controls = useAnimation();
  const containerRef = useRef();

  // Mouse move effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      const { clientX, clientY } = e;
      const { width, height } = containerRef.current.getBoundingClientRect();
      const x = (clientX - width / 2) / width;
      const y = (clientY - height / 2) / height;
      
      controls.start({
        x: x * 15,
        y: y * 15,
        transition: { duration: 0.5, ease: "easeOut" }
      });
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('mousemove', handleMouseMove);
      return () => container.removeEventListener('mousemove', handleMouseMove);
    }
  }, [controls]);

  const handleGoogleLogin = async () => {
    try {
      await loginWithGoogle();
    } catch (error) {
      console.error('Login failed:', error);
      toast.error('Đăng nhập thất bại');
    }
  };

  return (
    <div 
      ref={containerRef}
      className="min-h-screen relative overflow-hidden bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900"
    >
      {/* Three.js Background */}
      <div className="fixed inset-0 z-0">
        <Canvas camera={{ position: [0, 0, 5], fov: 75 }}>
          <LoginBackground />
          <OrbitControls enableZoom={false} enablePan={false} autoRotate autoRotateSpeed={0.3} />
        </Canvas>
      </div>

      {/* Animated Background Particles */}
      <div className="absolute inset-0 z-10">
        {[...Array(40)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full opacity-30"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -100, 0],
              opacity: [0.3, 0.8, 0.3],
            }}
            transition={{
              duration: 5 + Math.random() * 3,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      {/* Floating Orbs */}
      <div className="absolute inset-0 z-10">
        <motion.div
          className="absolute top-20 left-20 w-40 h-40 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20"
          animate={{
            scale: [1, 1.3, 1],
            rotate: [0, 180, 360],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute top-40 right-40 w-32 h-32 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-25"
          animate={{
            scale: [1.2, 1, 1.2],
            rotate: [360, 180, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute bottom-40 left-1/3 w-48 h-48 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-15"
          animate={{
            scale: [1, 1.4, 1],
            rotate: [0, -180, -360],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      {/* Main Content */}
      <div className="relative z-20 flex items-center justify-center min-h-screen px-4 sm:px-6 lg:px-8 py-12">
        <motion.div 
          className="max-w-md w-full"
          animate={controls}
        >
          <div className="text-center mb-8">
            {/* Logo Animation */}
            <motion.div
              initial={{ scale: 0, opacity: 0, rotate: -180 }}
              animate={{ scale: 1, opacity: 1, rotate: 0 }}
              transition={{ duration: 1, type: "spring", bounce: 0.6 }}
              className="mb-8"
            >
              <motion.div
                animate={{ 
                  boxShadow: [
                    "0 0 30px rgba(139, 92, 246, 0.5)",
                    "0 0 60px rgba(59, 130, 246, 0.8)",
                    "0 0 30px rgba(139, 92, 246, 0.5)"
                  ]
                }}
                transition={{ duration: 3, repeat: Infinity }}
                className="inline-block p-6 rounded-full bg-white/10 backdrop-blur-sm"
              >
                <img 
                  src="https://res.cloudinary.com/dypjamkif/image/upload/v1754457007/Dvt_Hulo_logo_copy_t6flpb.png" 
                  alt="DVT Hulo Logo" 
                  className="h-16 w-auto"
                />
              </motion.div>
            </motion.div>

            {/* Title with Glitch Effect */}
            <motion.h2
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-4xl sm:text-5xl font-bold mb-4 relative"
            >
              <motion.span 
                className="block bg-gradient-to-r from-purple-400 via-blue-500 to-pink-500 bg-clip-text text-transparent"
                animate={{
                  backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"]
                }}
                transition={{ duration: 4, repeat: Infinity }}
                style={{ backgroundSize: "200% 200%" }}
              >
                Đăng nhập
              </motion.span>
              <motion.span 
                className="block text-white"
                animate={{ 
                  textShadow: [
                    "0 0 10px rgba(255,255,255,0.5)",
                    "0 0 20px rgba(255,255,255,0.8)",
                    "0 0 10px rgba(255,255,255,0.5)"
                  ]
                }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                DVT Hulo
              </motion.span>
            </motion.h2>

            {/* Subtitle */}
            <motion.p
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-xl text-gray-200 mb-8"
            >
              Sử dụng tài khoản Google để đăng nhập
            </motion.p>
          </div>

          {/* Login Card */}
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl p-8 border border-white/20"
          >
            <div className="space-y-6">
              {/* Google Login Button */}
              <motion.button
                whileHover={{ 
                  scale: 1.05,
                  boxShadow: "0 20px 40px rgba(59, 130, 246, 0.4)"
                }}
                whileTap={{ scale: 0.95 }}
                onClick={handleGoogleLogin}
                className="w-full flex justify-center items-center px-6 py-4 bg-white text-gray-900 rounded-xl font-semibold text-lg shadow-2xl hover:shadow-blue-500/25 transition-all duration-300 overflow-hidden group relative"
              >
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 opacity-0 group-hover:opacity-10 transition-opacity duration-300"
                  initial={{ x: "-100%" }}
                  whileHover={{ x: "100%" }}
                  transition={{ duration: 0.6 }}
                />
                <div className="flex items-center space-x-4 relative z-10">
                  <svg className="w-6 h-6" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <span>Đăng nhập với Google</span>
                </div>
              </motion.button>

              {/* Features List */}
              <div className="space-y-4 pt-6 border-t border-white/20">
                <div className="grid grid-cols-1 gap-4">
                  {[
                    { icon: Shield, text: "Bảo mật cao với Google OAuth", color: "text-green-400" },
                    { icon: Zap, text: "Đăng nhập nhanh chóng", color: "text-yellow-400" },
                    { icon: User, text: "Quản lý tài khoản dễ dàng", color: "text-blue-400" }
                  ].map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ x: -50, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                      className="flex items-center space-x-3 p-3 rounded-lg bg-white/5 border border-white/10"
                    >
                      <motion.div
                        animate={{ 
                          scale: [1, 1.2, 1],
                          rotate: [0, 5, 0]
                        }}
                        transition={{ duration: 2, repeat: Infinity, delay: index * 0.5 }}
                        className={`flex-shrink-0 ${item.color}`}
                      >
                        <item.icon className="h-5 w-5" />
                      </motion.div>
                      <span className="text-white text-sm">{item.text}</span>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Terms and Privacy */}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 1.2 }}
                className="text-center pt-4"
              >
                <p className="text-sm text-gray-300">
                  Bằng cách đăng nhập, bạn đồng ý với{' '}
                  <a href="#" className="font-medium text-blue-400 hover:text-blue-300 transition-colors">
                    Điều khoản sử dụng
                  </a>{' '}
                  và{' '}
                  <a href="#" className="font-medium text-blue-400 hover:text-blue-300 transition-colors">
                    Chính sách bảo mật
                  </a>
                </p>
              </motion.div>
            </div>
          </motion.div>

          {/* Floating Elements */}
          <motion.div
            animate={{ 
              y: [0, -10, 0],
              rotate: [0, 5, 0]
            }}
            transition={{ 
              duration: 4, 
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="absolute top-1/4 left-10 w-4 h-4 bg-purple-400 rounded-full opacity-60"
          />
          <motion.div
            animate={{ 
              y: [0, 10, 0],
              rotate: [0, -5, 0]
            }}
            transition={{ 
              duration: 3, 
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="absolute top-1/3 right-20 w-3 h-3 bg-blue-400 rounded-full opacity-60"
          />
        </motion.div>
      </div>
    </div>
  );
};

export default Login; 
